import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../LanguageContext';
import SEO from '../components/SEO';

const imagesMeta = import.meta.glob('/src/assets/gallery/*.{webp,png,jpg}', {
  query: {
    w: '800;1200;1600',
    format: 'webp',
    q: '90',
    as: 'picture'
  },
  eager: true
});

const galleryItems = [
  { keyStr: "gazebos", id: "gazebos", images: [
    { file: "gazebo_5.webp", altKey: "alt-gazebo-5" },
    { file: "gazebo_1.webp", altKey: "alt-gazebo-1" },
    { file: "gazebo_2.webp", altKey: "alt-gazebo-2" },
    { file: "gazebo_3.webp", altKey: "alt-gazebo-3" },
    { file: "gazebo_4.webp", altKey: "alt-gazebo-4" },
    { file: "gazebo_6.webp", altKey: "alt-gazebo-6" },
    { file: "pergola_1.webp", altKey: "alt-pergola-1" }
  ]},
  { keyStr: "lights", id: "lights", images: [
    { file: "light_pole_1.webp", altKey: "alt-light-pole-1" },
    { file: "pole_2.webp", altKey: "alt-pole-2" },
    { file: "pole_3.webp", altKey: "alt-pole-3" }
  ]},
  { keyStr: "tables", id: "tables", images: [
    { file: "table_1_1772363825070.webp", altKey: "alt-table-1" },
    { file: "table_2_1772363848508.webp", altKey: "alt-table-2" },
    { file: "table_3_1772363862516.webp", altKey: "alt-table-3" }
  ]},
  { keyStr: "chairs", id: "chairs", images: [
    { file: "chair_1_1772363879194.webp", altKey: "alt-chair-1" },
    { file: "chair_2_1772363890939.webp", altKey: "alt-chair-2" },
    { file: "chair_3_1772363904466.webp", altKey: "alt-chair-3" },
    { file: "bench_1_1772363689648.webp", altKey: "alt-bench-1" },
    { file: "bench_2_1772363702318.webp", altKey: "alt-bench-2" },
    { file: "bench_3_1772363720364.webp", altKey: "alt-bench-3" }
  ]},
  { keyStr: "fences", id: "fences", images: [
    { file: "fence_2.webp", altKey: "alt-fence-2" },
    { file: "fence_1.webp", altKey: "alt-fence-1" },
    { file: "fence_3.webp", altKey: "alt-fence-3" },
    { file: "fence_4.webp", altKey: "alt-fence-4" }
  ]}
];

const getPictureProps = (imgObj) => {
  const imgName = typeof imgObj === 'string' ? imgObj : imgObj.file;
  const path = '/src/assets/gallery/' + imgName;
  if (imagesMeta[path]) return imagesMeta[path].default || imagesMeta[path];
  return { img: { src: '/src/assets/gallery/' + imgName }, sources: {} };
};

function Carousel({ images, title, t }) {
  const [idx, setIdx] = useState(0);
  const startX = useRef(0);

  const next = (e) => { e && e.stopPropagation(); setIdx((p) => (p + 1) % images.length); };
  const prev = (e) => { e && e.stopPropagation(); setIdx((p) => (p - 1 + images.length) % images.length); };

  const onTouchStart = (e) => { startX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    const diff = startX.current - e.changedTouches[0].clientX;
    if (diff > 40) next();
    if (diff < -40) prev();
  };

  const currentImg = images[idx];
  const pic = getPictureProps(currentImg);
  const altText = currentImg.altKey ? t(currentImg.altKey) : `${title} photo ${idx + 1}`;

  return (
    <div className="carousel-wrapper" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <picture>
        {pic.sources && pic.sources.webp && <source srcSet={pic.sources.webp} type="image/webp" sizes="(max-width: 660px) 800px, (max-width: 1100px) 1600px, 1600px" />}
        <img 
          src={pic.img.src} 
          alt={altText} 
          loading={idx === 0 ? "eager" : "lazy"} 
          className="carousel-img" 
        />
      </picture>
      <button onClick={prev} className="carousel-btn prev" aria-label="Previous">&#8249;</button>
      <button onClick={next} className="carousel-btn next" aria-label="Next">&#8250;</button>
      <div className="carousel-dots">
        {images.map((_, i) => (
          <button key={i} className={"dot" + (i === idx ? " active" : "")} aria-label={`Slide ${i + 1}`} onClick={() => setIdx(i)} />
        ))}
      </div>
    </div>
  );
}

export default function Gallery() {
  const { t } = useLanguage();
  const observer = useRef(null);

  useEffect(() => {
    observer.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.current.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    
    document.querySelectorAll('.fade-up').forEach(el => observer.current.observe(el));
    
    return () => observer.current && observer.current.disconnect();
  }, []);

  return (
    <main className="gallery-main">
      <SEO title={t('seo-gallery-title')} description={t('seo-gallery-desc')} />
      <div className="gallery-grid">
        {galleryItems.map((item) => (
          <div key={item.id} className="gallery-card fade-up">
            <Carousel images={item.images} title={t('gallery-title-' + item.keyStr)} t={t} />
            <div className="card-info">
              <h2 className="card-title">{t('gallery-title-' + item.keyStr)}</h2>
              <p className="card-description">{t('gallery-desc-' + item.keyStr)}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="gallery-footer fade-up">
        <Link to="/contact" className="gallery-contact-btn">
          {t('nav-contact')}
        </Link>
      </div>
    </main>
  );
}

